import React, { useState } from 'react';
import { Box, Typography, Button, Stack, Alert } from '@mui/material';
import { CloudUpload, Description } from '@mui/icons-material';
import { DocumentsGrid } from '../grids/DocumentsGrid';
import { UploadDocumentDialog } from '../dialogs/UploadDocumentDialog';
import { ViewDocumentDialog } from '../dialogs/ViewDocumentDialog';
import { WriteoffDocumentView } from '../../../../services/WriteoffService/types/documentTypes';
import { useListWriteoffDocumentsQuery } from '../../../../services/WriteoffService';
import { useAppSelector } from '../../../../app/store';
import SectionPaper from '../../../common/components/styled-ui-elements/SectionPaper';

export const DocumentsSection: React.FC = () => {
  const { currentWriteoff } = useAppSelector(state => state.writeoff);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<WriteoffDocumentView | null>(null);

  // Query for documents with real-time updates
  const { 
    data: documents = [], 
    isLoading, 
    error,
    refetch: refetchDocuments 
  } = useListWriteoffDocumentsQuery({
    company: currentWriteoff?.identifiers?.company || '',
    center: currentWriteoff?.identifiers?.center || '',
    writeoffId: currentWriteoff?.identifiers?.id || ''
  }, {
    skip: !currentWriteoff?.identifiers?.id
  });

  const handleUploadClick = () => {
    setIsUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setIsUploadDialogOpen(false);
    // Refetch documents after upload
    refetchDocuments();
  };

  const handleDocumentView = (document: WriteoffDocumentView) => {
    setSelectedDocument(document);
    setIsViewDialogOpen(true);
  };

  const handleViewDialogClose = () => {
    setIsViewDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleDocumentDeleted = (documentId: string) => {
    // Refetch documents after deletion
    refetchDocuments();
  };

  // Early return if no writeoff loaded
  if (!currentWriteoff) {
    return (
      <SectionPaper>
        <Alert severity="info">Loading writeoff information...</Alert>
      </SectionPaper>
    );
  }

  return (
    <>
      {/* Header Section */}
      <SectionPaper sx={{ mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Description sx={{ color: '#3b82f6', fontSize: 24 }} />
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 600, 
                  color: '#1e293b',
                  mb: 0.5
                }}
              >
                Documents
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: '#64748b' }}
              >
                {documents.length === 0 
                  ? 'No documents have been uploaded for this write-off yet' 
                  : `${documents.length} document${documents.length !== 1 ? 's' : ''} uploaded`
                }
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleUploadClick}
            sx={{
              backgroundColor: '#162346',
              fontWeight: 600,
              px: 3,
              '&:hover': {
                backgroundColor: '#1a2a52'
              }
            }}
          >
            Upload Documents
          </Button>
        </Stack>
      </SectionPaper>

      {/* Documents Grid Section */}
      <SectionPaper 
        sx={{ 
          border: '1px solid #e2e8f0',
          borderRadius: 1.5,
          overflow: 'hidden',
          p: 0
        }}
      >
        {documents.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Alert 
              severity="info" 
              sx={{ 
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                '& .MuiAlert-icon': {
                  color: '#0284c7'
                }
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                No documents available
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
                Click "Upload Documents" to add the first document for this write-off
              </Typography>
            </Alert>
          </Box>
        ) : (
          <DocumentsGrid
            writeoff={currentWriteoff}
            documents={documents}
            isLoading={isLoading}
            error={error}
            onDocumentView={handleDocumentView}
            onRefresh={refetchDocuments}
          />
        )}
      </SectionPaper>

      {/* Upload Dialog */}
      <UploadDocumentDialog
        open={isUploadDialogOpen}
        onClose={handleUploadDialogClose}
        writeoff={currentWriteoff}
      />

      {/* View Dialog */}
      <ViewDocumentDialog
        open={isViewDialogOpen}
        onClose={handleViewDialogClose}
        document={selectedDocument}
        writeoff={currentWriteoff}
        onDocumentDeleted={handleDocumentDeleted}
      />
    </>
  );
};

export default DocumentsSection;
